import { c as _c } from "react/compiler-runtime";
import { cn } from "@repo/ui/utils";
import { VisualWrapper } from "@/components/common/visual/visual.component";
import { MarketingPopupClose } from "./marketing-popup-close.component";
import { MarketingPopupContent } from "./marketing-popup-content.component";
import { popupVisual } from "./marketing-popup.types";
import type { MarketingPopupVariantProps } from "./marketing-popup.types";

/**
 * Smoked glass — the card is the pane, so the page and the dimmed backdrop
 * refract through it. Client pick over system grey (VAW-1647).
 *
 * Follows the house translucent-over-imagery idiom (`Tag`'s transparent-dark:
 * dark surface, blur, on-dark text) with design-system tokens throughout —
 * `dark` flips `surface-base-primary` to deep-sea-blue-800 and `border-subtle`
 * to its on-dark value, the same way the immersive treatment inverts.
 *
 * Two deliberate departures:
 *
 * - Dark rather than light. The modal always sits over a dark scrim, and a
 *   light pane over dark imagery lands mid-tone: opaque enough for dark body
 *   copy to stay legible is also opaque enough to kill the frost. A dark pane
 *   can stay genuinely translucent because on-dark text holds its contrast
 *   whatever shows through.
 * - A far wider blur than the 4–7px the codebase uses elsewhere. Those are
 *   small chrome (tags, the floating nav pill, the PDF loading veil) where a
 *   tight radius reads as frost; at this surface size the same radius reads as
 *   noise. Blur scales with the pane.
 *
 * No drop shadow — the dimmed backdrop already separates the card from the
 * page. The inset hairline is the pane's lit edge, not a lift.
 */
export function MarketingPopupGlass(t0) {
  const $ = _c(16);
  const {
    popup,
    className,
    onClose,
    onCtaClick
  } = t0;
  let t1;
  if ($[0] !== popup) {
    t1 = popupVisual(popup);
    $[0] = popup;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const visual = t1;
  let t2;
  if ($[2] !== className) {
    t2 = cn("dark relative w-full max-w-xl overflow-hidden rounded-md", "bg-surface-base-primary/45 backdrop-blur-[28px] backdrop-saturate-[180%]", "border border-border-subtle", "shadow-[inset_0_1px_0_rgba(255,255,255,0.28)]", className);
    $[2] = className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onClose) {
    t3 = <MarketingPopupClose className="border border-border-subtle bg-surface-base-primary/40 backdrop-blur-[8px]" onClick={onClose} />;
    $[4] = onClose;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== visual) {
    t4 = visual && <div className="relative aspect-[16/9] overflow-hidden"><VisualWrapper visual={visual} className="absolute inset-0 h-full w-full" visualClassName="h-full w-full" imgClassName="h-full w-full object-cover" asBackground={true} /></div>;
    $[6] = visual;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== onCtaClick || $[9] !== popup) {
    t5 = <MarketingPopupContent popup={popup} className="p-6 tablet:p-8" onCtaClick={onCtaClick} />;
    $[8] = onCtaClick;
    $[9] = popup;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t6 = <article className={t2} data-testid="marketing-popup-glass">{t3}{t4}{t5}</article>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}